import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
  useOperationSteps,
  useCreateOperationStep,
} from '@/services/queries/operation_steps'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useCADQuery, useResetGLTF } from '@/services/queries/cads'
import { useIsReadOnly } from '../../hooks/useIsReadOnly'
import { useCADPageStore } from '@/state'
import { useOpenStepOnLoad } from './hooks/useOpenStepOnLoad'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { StepCardList } from './StepCardList'
import { AddStepButton } from './AddStepButton'
import { AssemblyOrderHeader } from './AssemblyOrderHeader'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/utils'

export const AssemblyOrderPanel = () => {
  useOpenStepOnLoad()
  const { toast } = useToast()
  const { isLoading: isCadLoading, data: cadData } = useCADQuery()
  const { isLoading: isDocumentLoading, data: docData } = useDocumentPageQuery()
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()
  const { mutate: createOperationStep } = useCreateOperationStep({
    documentVersionId: docData?.documentVersion?.id,
  })
  const { isLoading: isReadOnlyLoading, data: isReadOnlyData } = useIsReadOnly()
  const subAssemblyDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId || null,
  )
  const activeOperationStep = useCADPageStore((state) => state.operationStep)
  const isEditModeActive = useCADPageStore(
    useShallow((state) => state.isEditModeActive),
  )
  const isRetakeModeActive = useCADPageStore(
    useShallow((state) => state.isRetakeModeActive),
  )
  const { reset: resetGLTF } = useResetGLTF()

  // Keep the array identity stable across unrelated rerenders.
  // SortableList resets internal order whenever `value` is a new reference.
  const operationSteps = useMemo(
    () => steps.filter((step) => step.parent === subAssemblyDocumentPageId),
    [steps, subAssemblyDocumentPageId],
  )

  const isLoading =
    isCadLoading || isDocumentLoading || isLoadingSteps || isReadOnlyLoading

  if (isLoading) {
    return (
      <div>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    )
  }

  const documentVersionId = cadData.documentVersion?.id
  const documentType = docData?.documentType
  const isReadOnly = Boolean(isReadOnlyData?.isReadOnly)

  return (
    <div
      className={cn(
        'bg-gray-100 flex flex-col h-full w-full py-4 px-2 space-y-2 border-[3px] rounded-xl relative',
        {
          'border-gray-300': !activeOperationStep && !isEditModeActive,
          'border-primary-50':
            activeOperationStep && !isEditModeActive && !isRetakeModeActive,
          'opacity-40 pointer-events-none': isRetakeModeActive,
        },
      )}
      data-testid="steps-list"
    >
      <div className="text-sm font-semibold px-2">
        {activeOperationStep && !isEditModeActive && !isRetakeModeActive && (
          <Button
            variant="outline"
            size="sm"
            className="absolute top-0 right-0 bg-white border-2 border-primary-50 z-50 text-primary-50 h-5 hover:text-primary-50"
            onClick={resetGLTF}
            style={{ translate: '-30% -50%' }}
          >
            ESC
          </Button>
        )}
      </div>
      <AssemblyOrderHeader />
      <StepCardList
        documentVersionId={documentVersionId as string}
        documentId={docData?.doc?.id as string}
        steps={operationSteps}
        isReadOnly={isReadOnly}
      />
      <div className="flex justify-end">
        <AddStepButton
          isReadOnly={isReadOnly}
          documentType={documentType}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={async () => {
            if (documentVersionId) {
              const { documentPage: step } = await createOperationStep({
                documentVersionId,
                parentDocumentId: subAssemblyDocumentPageId,
                orderNumber: operationSteps.length + 1,
              })

              if (step) {
                toast({
                  title: `New operation added`,
                  variant: 'success',
                })
              }
            }
          }}
        />
      </div>
    </div>
  )
}
